/**
 * AccountLayout - "My account" area on the shared SectionShell (same frame as the main app).
 */
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { ACCOUNT_NAV } from "@/components/layout/nav/account-nav";
import { SectionShell } from "@/components/layout/SectionShell";
import { getUserDisplayName, getUserInitials, getUserRoleLabel, type ShellNavItem } from "@/components/layout/shell";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { PersonAvatar } from "@/components/shared/person-avatar";

function AccountSummaryCard() {
  const { user } = useAuth();
  const mfaOn = !!user?.mfa_enabled;

  return (
    <div className="rounded-[24px] bg-card p-4 shadow-[0_1px_2px_rgba(0,58,67,0.05)]">
      <div className="flex items-center gap-3">
        <PersonAvatar src={user?.avatar_url} initials={getUserInitials(user)} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{getUserDisplayName(user)}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Role</span>
          <span className="font-medium text-foreground">{getUserRoleLabel(user?.role)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Two-step sign-in</span>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
              mfaOn ? "bg-[#e6f0ee] text-[#004952]" : "bg-[#ffe9e1] text-[#c4532f]",
            )}
          >
            {mfaOn ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
            {mfaOn ? "On" : "Off"}
          </span>
        </div>
      </div>
      {!mfaOn && (
        <Link
          to="/account/security"
          className="mt-4 flex h-9 items-center justify-center rounded-full bg-[#f8f3eb] text-sm font-semibold text-[#003a43] transition-colors hover:bg-[#f1ece2] dark:bg-muted dark:text-foreground"
        >
          Turn on two-step sign-in
        </Link>
      )}
    </div>
  );
}

function accountItemActive(pathname: string, item: ShellNavItem): boolean {
  return pathname === item.href;
}

export default function AccountLayout() {
  return (
    <SectionShell
      title="My account"
      sections={ACCOUNT_NAV}
      isItemActive={accountItemActive}
      aside={<AccountSummaryCard />}
    />
  );
}
